import React, { useEffect, useState, useMemo } from "react";
import {
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import ReactApexChart from "react-apexcharts";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import { toast } from "react-toastify";
import styles from "./InboundFunnel.module.css";

const WINDOW_OPTIONS = [
  { value: 7, label: "Last 7 days" },
  { value: 30, label: "Last 30 days" },
  { value: 60, label: "Last 60 days" },
  { value: 90, label: "Last 90 days" },
];

// Friendly labels for the technical source keys used by the capture
// endpoint. Anything not in the map renders as-is.
const SOURCE_LABELS = {
  blog_exit_intent: "Blog · Exit intent",
  blog_content_upgrade: "Blog · Content upgrade",
  blog_footer_subscribe: "Blog · Footer subscribe",
  csv_audit: "Free SaaS Audit · CSV",
  compare_page_cta: "Compare page CTA",
  newsletter_subscribe: "Newsletter subscribe",
  other: "Other",
};

const InboundFunnel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(30);
  const [emailSearch, setEmailSearch] = useState("");

  const fetchData = async (override) => {
    setLoading(true);
    try {
      const w = override ?? days;
      const res = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/admin/inbound_funnel?days=${w}`,
        { credentials: "include" },
      );
      const json = await res.json();
      if (json?.status === 200) setData(json.data);
      else toast.error(json?.message || "Failed to load funnel");
    } catch (e) {
      console.error("[inbound_funnel]", e);
      toast.error("Failed to load funnel");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(days);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  const funnel = data?.funnel || [];
  const leads = data?.leads || { total: 0, by_source: [] };
  const warm = data?.warm_leads || {};
  const content = data?.content || {};

  // Bar chart: daily captures, stacked by source.
  const chartData = useMemo(() => {
    if (!data?.daily_leads) return { options: {}, series: [] };
    const dates = Array.from(
      new Set(data.daily_leads.map((r) => r.date)),
    ).sort();
    const sources = Array.from(
      new Set(data.daily_leads.map((r) => r.source)),
    );
    const series = sources.map((source) => ({
      name: SOURCE_LABELS[source] || source,
      data: dates.map((d) => {
        const row = data.daily_leads.find(
          (x) => x.date === d && x.source === source,
        );
        return row ? Number(row.count) : 0;
      }),
    }));
    return {
      options: {
        chart: {
          type: "bar",
          stacked: true,
          toolbar: { show: false },
          fontFamily: "League Spartan, sans-serif",
        },
        plotOptions: { bar: { borderRadius: 4, columnWidth: "70%" } },
        dataLabels: { enabled: false },
        legend: {
          position: "top",
          horizontalAlign: "right",
          fontSize: "12px",
        },
        grid: { borderColor: "#eef3f8" },
        colors: ["#1c9aea", "#134f8b", "#0f1b2c", "#7953ff", "#c0392b", "#1f8a3a"],
        xaxis: {
          categories: dates,
          labels: { style: { colors: "#8696aa", fontSize: "11px" } },
        },
        yaxis: {
          labels: {
            style: { colors: "#8696aa", fontSize: "11px" },
            formatter: (v) => Math.round(v),
          },
          title: {
            text: "Captures per day",
            style: { color: "#8696aa", fontSize: "11px", fontWeight: 600 },
          },
        },
        tooltip: { x: { format: "dd MMM" } },
      },
      series,
    };
  }, [data]);

  const maxBySource = Math.max(
    1,
    ...(leads.by_source || []).map((r) => Number(r.count) || 0),
  );

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.titleBlock}>
          <h1>Inbound Funnel</h1>
          <div className={styles.subtitle}>
            Captures → signups → activations → first purchase. Per-channel
            breakdowns + daily trend. Window picker on the right.
          </div>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <FormControl
            size="small"
            sx={{ minWidth: 160, background: "#fff", borderRadius: "999px" }}
          >
            <InputLabel id="window-label">Window</InputLabel>
            <Select
              labelId="window-label"
              label="Window"
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              sx={{ borderRadius: "999px" }}
            >
              {WINDOW_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            className={styles.refreshBtn}
            startIcon={<RefreshOutlinedIcon />}
            onClick={() => fetchData()}
          >
            Refresh
          </Button>
        </div>
      </div>

      {loading && !data ? (
        <div className={styles.loaderWrap}>
          <CircularProgress />
        </div>
      ) : !data ? (
        <div className={styles.card}>No funnel data yet.</div>
      ) : (
        <>
          {/* Funnel cards */}
          <div className={styles.funnelRow}>
            {funnel.map((step, i) => (
              <div className={styles.funnelCard} key={step.label}>
                <div className={styles.funnelLabel}>
                  Step {i + 1} · {step.label}
                </div>
                <div className={styles.funnelValue}>{step.value}</div>
                {i > 0 && (
                  <div className={styles.funnelRate}>
                    {step.rate_pct ?? 0}% of prev. step
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Channel breakdown + warm-leads + content stats */}
          <div className={styles.columns}>
            <div className={styles.card}>
              <h3 className={styles.cardTitle}>Captures by channel</h3>
              <p className={styles.cardSub}>
                {leads.total} email captures in window · {leads.converted_to_signup} converted to signup
              </p>
              {(leads.by_source || []).map((r) => (
                <div key={r.source} className={styles.sourceRow}>
                  <div className={styles.sourceLabel}>
                    {SOURCE_LABELS[r.source] || r.source}
                  </div>
                  <div className={styles.sourceBar}>
                    <div
                      className={styles.sourceFill}
                      style={{
                        width: `${(Number(r.count) / maxBySource) * 100}%`,
                      }}
                    />
                  </div>
                  <div className={styles.sourceCount}>{r.count}</div>
                </div>
              ))}
              {(!leads.by_source || leads.by_source.length === 0) && (
                <div style={{ color: "#8696aa", fontSize: 13 }}>
                  No captures in this window yet.
                </div>
              )}
            </div>

            <div className={styles.card}>
              <h3 className={styles.cardTitle}>Warm leads (newsletter)</h3>
              <p className={styles.cardSub}>
                Engaged readers extracted from Zoho Campaigns
              </p>
              <div className={styles.statGrid}>
                <div className={styles.statBox}>
                  <div className={styles.statLabel}>Pending</div>
                  <div className={styles.statValue}>{warm.pending ?? 0}</div>
                </div>
                <div className={styles.statBox}>
                  <div className={styles.statLabel}>Sent</div>
                  <div className={styles.statValue}>{warm.sent ?? 0}</div>
                </div>
                <div className={styles.statBox}>
                  <div className={styles.statLabel}>Replied</div>
                  <div className={styles.statValue}>{warm.replied ?? 0}</div>
                  <div className={styles.statSub}>
                    {warm.sent
                      ? `${Math.round(((warm.replied ?? 0) / warm.sent) * 100)}% reply rate`
                      : ""}
                  </div>
                </div>
                <div className={styles.statBox}>
                  <div className={styles.statLabel}>Skipped</div>
                  <div className={styles.statValue}>{warm.skipped ?? 0}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Content + daily chart */}
          <div className={styles.columns}>
            <div className={styles.card}>
              <h3 className={styles.cardTitle}>Daily captures</h3>
              <p className={styles.cardSub}>Stacked by source</p>
              {chartData.series.length > 0 ? (
                <ReactApexChart
                  options={chartData.options}
                  series={chartData.series}
                  type="bar"
                  height={260}
                />
              ) : (
                <div style={{ color: "#8696aa", padding: "40px 0", textAlign: "center" }}>
                  No captures to chart yet.
                </div>
              )}
            </div>

            <div className={styles.card}>
              <h3 className={styles.cardTitle}>Content production</h3>
              <p className={styles.cardSub}>SEO-driving surface area</p>
              <div className={styles.statGrid}>
                <div className={styles.statBox}>
                  <div className={styles.statLabel}>Blogs published</div>
                  <div className={styles.statValue}>
                    {content.blogs_published_total ?? 0}
                  </div>
                  <div className={styles.statSub}>
                    +{content.blogs_published_in_window ?? 0} in window
                  </div>
                </div>
                <div className={styles.statBox}>
                  <div className={styles.statLabel}>Blog views (all-time)</div>
                  <div className={styles.statValue}>
                    {content.blogs_views_total ?? 0}
                  </div>
                </div>
                <div className={styles.statBox}>
                  <div className={styles.statLabel}>Compare pages</div>
                  <div className={styles.statValue}>
                    {content.compare_published_total ?? 0}
                  </div>
                  <div className={styles.statSub}>
                    +{content.compare_published_in_window ?? 0} in window
                  </div>
                </div>
                <div className={styles.statBox}>
                  <div className={styles.statLabel}>Compare views (all-time)</div>
                  <div className={styles.statValue}>
                    {content.compare_views_total ?? 0}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Captured emails — the actual list behind the count */}
          <div className={styles.card} style={{ marginTop: 16 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 12,
              }}
            >
              <div>
                <h3 className={styles.cardTitle}>Captured emails</h3>
                <p className={styles.cardSub}>
                  {leads.total} email{leads.total === 1 ? "" : "s"} captured in window
                  {leads.list_capped ? " · showing newest 1000" : ""}
                </p>
              </div>
              <input
                type="text"
                value={emailSearch}
                onChange={(e) => setEmailSearch(e.target.value)}
                placeholder="Search email or source…"
                style={{
                  padding: "8px 12px",
                  border: "1px solid #d7dee6",
                  borderRadius: 8,
                  fontSize: 13,
                  minWidth: 220,
                }}
              />
            </div>

            {(() => {
              const q = emailSearch.trim().toLowerCase();
              const rows = (leads.list || []).filter(
                (r) =>
                  !q ||
                  (r.email || "").toLowerCase().includes(q) ||
                  (r.source || "").toLowerCase().includes(q),
              );
              if ((leads.list || []).length === 0) {
                return (
                  <div style={{ color: "#8696aa", fontSize: 13, padding: "16px 0" }}>
                    No captured emails in this window yet.
                  </div>
                );
              }
              return (
                <div style={{ maxHeight: 420, overflowY: "auto", marginTop: 12 }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead>
                      <tr style={{ textAlign: "left", color: "#8696aa" }}>
                        <th style={{ padding: "8px 8px", position: "sticky", top: 0, background: "#fff" }}>Email</th>
                        <th style={{ padding: "8px 8px", position: "sticky", top: 0, background: "#fff" }}>Source</th>
                        <th style={{ padding: "8px 8px", position: "sticky", top: 0, background: "#fff" }}>Captured</th>
                        <th style={{ padding: "8px 8px", position: "sticky", top: 0, background: "#fff" }}>Signed up</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r, i) => (
                        <tr key={`${r.email}-${i}`} style={{ borderTop: "1px solid #eef2f6" }}>
                          <td style={{ padding: "8px 8px", fontWeight: 500 }}>{r.email}</td>
                          <td style={{ padding: "8px 8px", color: "#5b6b7c" }}>
                            {SOURCE_LABELS[r.source] || r.source || "—"}
                          </td>
                          <td style={{ padding: "8px 8px", color: "#5b6b7c" }}>
                            {r.createdAt ? new Date(r.createdAt).toLocaleDateString() : "—"}
                          </td>
                          <td style={{ padding: "8px 8px" }}>
                            {r.converted ? (
                              <span style={{ color: "#1a8f4c", fontWeight: 600 }}>Yes</span>
                            ) : (
                              <span style={{ color: "#8696aa" }}>—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                      {rows.length === 0 && (
                        <tr>
                          <td colSpan={4} style={{ padding: "16px 8px", color: "#8696aa" }}>
                            No emails match “{emailSearch}”.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              );
            })()}
          </div>
        </>
      )}
    </div>
  );
};

export default InboundFunnel;
